import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams
} from "react-router-dom";
import { ErrorBoundary } from "@sentry/react";
import { useNavigateWithCommandClick } from "./hooks/useNavigateWithCommandClick";
import styled from "styled-components";
import GlobalStyles from "./GlobalStyles";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Settings from "./pages/Settings";
import ChangePassword from "./pages/ChangePassword";
import Admin from "./pages/Admin";
import AdminThread from "./pages/AdminThread";
import BotSelection from "./pages/BotSelection";
import Messages from "./pages/Messages";
import StatePage from "./pages/StatePage";
import StateEbtTheftPage from "./pages/StateEbtTheftPage";
import StatePage2 from "./pages/StatePage2";
import MobileSimulator from "./pages/MobileSimulator";
import ImageUpload from "./pages/Vision";
import Logo from "./components/Logo";
import Button from "./components/Button";
import { API_ENDPOINTS } from "./config/api";

// Styled Components
const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  font-size: 1.25rem;
  color: white;
`;

const AuthContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
  max-width: 400px;
  margin: 20px;
`;

const AuthHeader = styled.div`
  background: #ffffff;
  color: #333333;
  padding: 2rem 2rem 0 2rem;
  text-align: center;
`;

const AuthToggle = styled.div`
  display: flex;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 1rem;
  padding: 4px;
  gap: 4px;
  margin-top: 2rem;
`;

// Protected Route Component
const ProtectedRoute = ({ children, user }) => {
  const location = useLocation();

  if (!user) {
    // Store the current location for redirect after login
    console.log("ProtectedRoute: Storing redirect URL:", location.pathname);
    localStorage.setItem("redirectAfterLogin", location.pathname);
    return <Navigate to="/login" />;
  }

  return children;
};

// Auth Route Component (redirects to dashboard if already logged in)
const AuthRoute = ({ children, user }) => {
  return user ? <Navigate to="/dashboard" /> : children;
};

// Auth Layout Component
const AuthLayout = ({ children, onLogin }) => {
  const location = useLocation();
  const navigate = useNavigateWithCommandClick();
  const currentPath = location.pathname;

  return (
    <AppContainer>
      <AuthContainer>
        <AuthHeader>
          <Logo />
          <AuthToggle>
            <Button
              variant="auth-toggle"
              active={currentPath === "/login"}
              onClick={() => navigate("/login")}
            >
              Login
            </Button>
            <Button
              variant="auth-toggle"
              active={
                currentPath === "/register" || currentPath === "/create-account"
              }
              onClick={() => navigate("/create-account")}
            >
              Create account
            </Button>
          </AuthToggle>
        </AuthHeader>
        {children}
      </AuthContainer>
    </AppContainer>
  );
};

// Change Password Layout Component (similar to AuthLayout but without toggle)
const ChangePasswordLayout = ({ children, onLogin }) => {
  return (
    <AppContainer>
      <AuthContainer>
        <AuthHeader>
          <Logo />
        </AuthHeader>
        {children}
      </AuthContainer>
    </AppContainer>
  );
};

// Settings Wrapper Component
const SettingsWrapper = ({ user, onLogout }) => {
  const navigate = useNavigateWithCommandClick();

  return (
    <Settings
      user={user}
      onBack={() => navigate("/dashboard")}
      onLogout={onLogout}
    />
  );
};

// Admin Wrapper Component
const AdminWrapper = ({ user, onLogout }) => {
  const navigate = useNavigateWithCommandClick();

  // Check if user needs to change password and redirect to change password page
  useEffect(() => {
    if (user?.requirePasswordChange) {
      navigate("/change-password");
    }
  }, [user, navigate]);

  // Don't render admin if password change is required
  if (user?.requirePasswordChange) {
    return null;
  }

  return (
    <Admin
      user={user}
      onBack={() => navigate("/dashboard")}
      onLogout={onLogout}
    />
  );
};

// Bot Selection Wrapper Component (now the main dashboard)
const DashboardWrapper = ({ user, onLogout }) => {
  const navigate = useNavigateWithCommandClick();

  // Check if user needs to change password and redirect to change password page
  useEffect(() => {
    if (user?.requirePasswordChange) {
      navigate("/change-password");
    }
  }, [user, navigate]);

  const handleBotSelect = (bot, event) => {
    if (bot.vision) {
      navigate(`/vision/${bot.id}`, {}, event);
    } else {
      navigate(`/bots/${bot.id}`, {}, event);
    }
  };

  // Don't render dashboard if password change is required
  if (user?.requirePasswordChange) {
    return null;
  }

  return (
    <BotSelection
      user={user}
      onSelectBot={handleBotSelect}
      onLogout={onLogout}
    />
  );
};

// Bot Chat Wrapper Component for individual bot routes
const BotChatWrapper = ({ user, onLogout }) => {
  const { botId } = useParams();
  const navigate = useNavigateWithCommandClick();
  const [selectedBot, setSelectedBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // Check if user needs to change password and redirect to change password page
  useEffect(() => {
    if (user?.requirePasswordChange) {
      navigate("/change-password");
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchBotData = async () => {
      try {
        // Use the bot info endpoint with username to check access permissions
        const botInfoUrl = `${API_ENDPOINTS.bot(botId)}?username=${encodeURIComponent(user?.username || "")}`;
        const response = await fetch(botInfoUrl);
        
        if (response.ok) {
          const data = await response.json();
          if (data.bot) {
            setSelectedBot(data.bot);
          } else {
            // Bot not found, redirect to dashboard
            navigate("/dashboard");
          }
        } else if (response.status === 403) {
          // User doesn't have access to this bot
          setAccessDenied(true);
        } else {
          // Bot not found or other error, redirect to dashboard
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Error fetching bot:", error);
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchBotData();
    }
  }, [botId, navigate, user?.username]);

  // Don't render if password change is required
  if (user?.requirePasswordChange) {
    return null;
  }

  if (loading) {
    return (
      <AppContainer>
        <LoadingContainer>Loading bot...</LoadingContainer>
      </AppContainer>
    );
  }

  // Redirect to dashboard if access is denied
  if (accessDenied) {
    return <Navigate to="/dashboard" />;
  }

  if (!selectedBot) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <Messages
      user={user}
      selectedBot={selectedBot}
      token={localStorage.getItem("token")}
      onBack={() => navigate(-1)}
      onLogout={onLogout}
    />
  );
};

// State Page Wrapper Component
const StatePageWrapper = ({ user, onLogout }) => {
  const navigate = useNavigateWithCommandClick();

  // Check if user needs to change password and redirect to change password page
  useEffect(() => {
    if (user?.requirePasswordChange) {
      navigate("/change-password");
    }
  }, [user, navigate]);

  // Don't render if password change is required
  if (user?.requirePasswordChange) {
    return null;
  }

  return <StatePage user={user} onLogout={onLogout} />;
};

// State EBT Theft Page Wrapper Component
const StateEbtTheftPageWrapper = ({ user, onLogout }) => {
  const navigate = useNavigateWithCommandClick();

  // Check if user needs to change password and redirect to change password page
  useEffect(() => {
    if (user?.requirePasswordChange) {
      navigate("/change-password");
    }
  }, [user, navigate]);

  // Don't render if password change is required
  if (user?.requirePasswordChange) {
    return null;
  }

  return <StateEbtTheftPage user={user} onLogout={onLogout} />;
};

// State Page 2 Wrapper Component
const StatePage2Wrapper = ({ user, onLogout }) => {
  const navigate = useNavigateWithCommandClick();

  // Check if user needs to change password and redirect to change password page
  useEffect(() => {
    if (user?.requirePasswordChange) {
      navigate("/change-password");
    }
  }, [user, navigate]);

  // Don't render if password change is required
  if (user?.requirePasswordChange) {
    return null;
  }

  return <StatePage2 user={user} onLogout={onLogout} />;
};

const MobileSimulatorWrapper = ({ user, onLogout }) => {
  const { type: urlType } = useParams();
  const navigate = useNavigateWithCommandClick();

  // Check if user needs to change password and redirect to change password page
  useEffect(() => {
    if (user?.requirePasswordChange) {
      navigate("/change-password");
    }
  }, [user, navigate]);

  // Don't render if password change is required
  if (user?.requirePasswordChange) {
    return null;
  }

  return <MobileSimulator user={user} onLogout={onLogout} urlType={urlType} />;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await fetch(API_ENDPOINTS.profile, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);

            // User data loaded successfully, no additional action needed
            // The routing will handle redirecting to change password if needed
          } else {
            // Token is invalid, remove it
            localStorage.removeItem("token");
            setToken(null);
          }
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("token");
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("token", authToken);

    // Handle redirect after setting user state
    const redirectUrl = localStorage.getItem("redirectAfterLogin");
    console.log("handleLogin: Checking for redirect URL:", redirectUrl);

    if (redirectUrl) {
      console.log(
        "handleLogin: Found redirect URL, will redirect to:",
        redirectUrl
      );
      localStorage.removeItem("redirectAfterLogin");

      // Use setTimeout to ensure state is updated first
      setTimeout(() => {
        console.log("handleLogin: Executing redirect to:", redirectUrl);
        window.location.href = redirectUrl;
      }, 100);
    } else {
      console.log("handleLogin: No redirect URL, will use normal flow");
    }
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  if (loading) {
    return (
      <>
        <GlobalStyles />
        <AppContainer>
          <LoadingContainer>Loading...</LoadingContainer>
        </AppContainer>
      </>
    );
  }

  return (
    <>
      <GlobalStyles />
      <ErrorBoundary fallback={<div>An error has occurred</div>}>
        <Router>
          <Routes>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute user={user}>
                  <DashboardWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Root Route - Redirect to dashboard or change password if password change required */}
            <Route
              path="/"
              element={
                user ? (
                  user.requirePasswordChange ? (
                    <Navigate to="/change-password" />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Change Password Route - Protected */}
            <Route
              path="/change-password"
              element={
                <ProtectedRoute user={user}>
                  <ChangePasswordLayout onLogin={handleLogin}>
                    <ChangePassword user={user} onLogin={handleLogin} />
                  </ChangePasswordLayout>
                </ProtectedRoute>
              }
            />

            {/* Settings Route - Protected */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute user={user}>
                  <SettingsWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes - Protected */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute user={user}>
                  <AdminWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Admin Chat Logs with Domain, UUID Filter, and Bot ID - Protected (Most specific) */}
            <Route
              path="/admin/chat-logs/:domain/:uuidFilter/:botId"
              element={
                <ProtectedRoute user={user}>
                  <AdminWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Admin Chat Logs with Domain and UUID Filter - Protected */}
            <Route
              path="/admin/chat-logs/:domain/:uuidFilter"
              element={
                <ProtectedRoute user={user}>
                  <AdminWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Admin Chat Logs with Domain Filter - Protected (Least specific) */}
            <Route
              path="/admin/chat-logs/:domain"
              element={
                <ProtectedRoute user={user}>
                  <AdminWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Admin Bots with Filter - Protected */}
            <Route
              path="/admin/bots/:filter"
              element={
                <ProtectedRoute user={user}>
                  <AdminWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/:tab"
              element={
                <ProtectedRoute user={user}>
                  <AdminWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Admin Thread Route - Protected */}
            <Route
              path="/admin/thread/:threadId"
              element={
                <ProtectedRoute user={user}>
                  <AdminThread />
                </ProtectedRoute>
              }
            />

            {/* Individual Bot Route - Protected */}
            <Route
              path="/bots/:botId"
              element={
                <ProtectedRoute user={user}>
                  <BotChatWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Individual Bot with Thread Route - Protected */}
            <Route
              path="/bots/:botId/thread/:threadId"
              element={
                <ProtectedRoute user={user}>
                  <BotChatWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* State Page Route - Protected */}
            <Route
              path="/state/:stateName"
              element={
                <ProtectedRoute user={user}>
                  <StatePageWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* State EBT Theft Page Route - Protected */}
            <Route
              path="/state/:stateName/ebt-theft"
              element={
                <ProtectedRoute user={user}>
                  <StateEbtTheftPageWrapper
                    user={user}
                    onLogout={handleLogout}
                  />
                </ProtectedRoute>
              }
            />

            {/* State V2 Page Route - Protected */}
            <Route
              path="/state/:stateName/v2"
              element={
                <ProtectedRoute user={user}>
                  <StatePage2Wrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Mobile Simulator Page Route - Protected */}
            <Route
              path="/mobile-simulator/:type?"
              element={
                <ProtectedRoute user={user}>
                  <MobileSimulatorWrapper user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Vision Route with Bot ID - Protected */}
            <Route
              path="/vision/:botId"
              element={
                <ProtectedRoute user={user}>
                  <ImageUpload user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Auth Routes - Redirect to dashboard if logged in */}
            <Route
              path="/login"
              element={
                <AuthRoute user={user}>
                  <AuthLayout onLogin={handleLogin}>
                    <Login onLogin={handleLogin} />
                  </AuthLayout>
                </AuthRoute>
              }
            />
            <Route
              path="/register"
              element={
                <AuthRoute user={user}>
                  <AuthLayout onLogin={handleLogin}>
                    <Register onLogin={handleLogin} />
                  </AuthLayout>
                </AuthRoute>
              }
            />
            <Route
              path="/create-account"
              element={
                <AuthRoute user={user}>
                  <AuthLayout onLogin={handleLogin}>
                    <Register onLogin={handleLogin} />
                  </AuthLayout>
                </AuthRoute>
              }
            />

            {/* Catch all other routes */}
            <Route
              path="*"
              element={
                user ? <Navigate to="/dashboard" /> : <Navigate to="/login" />
              }
            />
          </Routes>
        </Router>
      </ErrorBoundary>
    </>
  );
}

export default App;
